/** Support profile — editable name + read-only account details + change password. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { getMe, patchProfile } from "../../api/patient";
import { describeError } from "../../api/errors";
import { ChangePasswordCard } from "../../components/ChangePasswordCard";

export function SupportProfile() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const [fullName, setFullName] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setFullName(data.full_name);
  }, [data]);

  const save = useMutation({
    mutationFn: () => patchProfile({ full_name: fullName.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setError(describeError(e)),
  });

  if (!data) return <p className="text-sm text-gray-400">Loading…</p>;

  const dirty = fullName.trim() !== data.full_name;

  return (
    <div className="max-w-lg space-y-8">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">My profile</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (fullName.trim()) save.mutate();
          }}
          className="space-y-4 rounded-xl border bg-white p-5 shadow-xs"
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-500">Full name</span>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="w-full rounded-lg border px-3 py-1.5"
            />
          </label>
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <Info label="Role" value={data.role} />
            <Info label="Email" value={data.email ?? "—"} />
            <Info label="Phone" value={data.phone ?? "—"} />
          </dl>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={save.isPending || !dirty || !fullName.trim()}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
            {saved && <span className="text-sm text-emerald-600">Saved</span>}
          </div>
        </form>
      </div>

      <ChangePasswordCard passwordSet={data.password_set} />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-gray-400">{label}</dt>
      {/* A long email wraps in its column instead of widening the page (RESP-35). */}
      <dd className="font-medium wrap-anywhere">{value}</dd>
    </div>
  );
}
